'use client';

import Image from 'next/image';
import Button from '../Button/Button';
import Link from 'next/link';
import { MessageType } from '~/definitions/message';
import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';
import { FREETEXT_KEYS } from '~/const/freetext';
import SafeHTML from '../SafeHTML/SafeHTML';

// Use string paths for images in public folder
const ThumbIcon = '/img/thumb_icon.png';
const ThumbDownIcon = '/img/thumb_down_icon.png';
const CoopLittDittLogo = '/img/coop_logo_litt_ditt.png';

interface Props {
    type: MessageType;
    title: string;
    message?: string;
    testId?: string;
    extendedClassName?: string;
    buttonText: string;
    buttonTestId?: string;
    url?: string;
    closeBtnText?: string;
}

interface InternalProps extends Props {
    renderCloseButton?: boolean;
}

const SubmitVoteMessage = (props: Props & InternalProps) => {
    const {
        type = 'message',
        title,
        message,
        extendedClassName,
        testId,
        buttonText,
        buttonTestId,
        url = '',
        renderCloseButton,
        closeBtnText,
    } = props;

    const icon = type === 'error' ? ThumbDownIcon : ThumbIcon;

    const titleBaseClassName = 'font-coopGroteskBold text-[1.5em] md:text-[2em]';
    const titleErrorClassName = type === 'error' ? 'text-custom-red-1' : '';
    const titleClassName = [titleBaseClassName, titleErrorClassName].filter(Boolean).join(' ');

    const logoClassNames = `flex flex-col w-full items-center text-center`;
    const containerClassNames = `flex w-full flex-col items-center`;
    const wrapperClassNames = `w-full lg:w-3/4 lg:max-w-[73rem] mb-[30px]`;
    const contentClassNames = `flex flex-col items-center bg-custom-grey-3 px-[20px] py-[20px] mx-[20px] mb-[30px] text-center ${extendedClassName ?? ''}`;
    const linkClassNames = `btn shadow-none min-h-[43px] min-w-[170px] text-white font-bold text-base no-underline`;
    const linkBorderClassNames = `bg-[#003f69] border-[#003f69] rounded-[24px]`;
    const linkHoverClassNames = `hover:text-[#c5f0f6] hover:bg-[#003254] visited:text-white`;
    const fullLinkClassNames = `${linkClassNames} ${linkBorderClassNames} ${linkHoverClassNames}`;

    return (
        <div className={containerClassNames} data-test-id={testId}>
            <div className={wrapperClassNames}>
                <div className={contentClassNames}>
                    <SafeHTML className={titleClassName} html={title} />
                    <div className="mx-5 mb-5 mt-10">
                        <Image src={icon} alt="" width={310} height={312} />
                    </div>
                    {message && type === 'message' && (
                        <SafeHTML className="font-coopGroteskBold md:text-[2em]" html={message} />
                    )}
                    {buttonText && (
                        <div className="my-[15px]">
                            {renderCloseButton ? (
                                <Button
                                    btnText={closeBtnText || 'Lukk stemmeseddel'}
                                    testId={'close-vote-window'}
                                    onClick={() => window.close()}
                                    variant={'success'}
                                />
                            ) : (
                                <a
                                    className={fullLinkClassNames}
                                    title={buttonText}
                                    data-test-id={buttonTestId ?? 'submit-vote-message-button'}
                                    href={url}
                                >
                                    <SafeHTML tag="span" html={buttonText} />
                                </a>
                            )}
                        </div>
                    )}
                </div>
                <div className={logoClassNames}>
                    <Link href="http://www.coop.no" target="_blank" title="min medlemskonto" className="coop-logo">
                        <Image
                            src={CoopLittDittLogo}
                            alt="min medlemskonto"
                            className="w-[150px]"
                            width={150}
                            height={74}
                        />
                    </Link>
                </div>
            </div>
        </div>
    );
};

const SubmitVoteMessageWithStore = observer(() => {
    const { ballotStore, voteStore, freetextStore } = getStore();
    const isStandAdmin = ballotStore.getIsStandAdmin();
    const voteStatus = voteStore.voteStatus;
    const validKeys = ['success', 'error'];
    // Fallback to 'error' if voteStatus is not a valid key
    const key = validKeys.includes(voteStatus) ? voteStatus : 'error';

    const type = key === 'success' ? 'message' : 'error';
    const title = freetextStore.getFreetextValueByKey(
        FREETEXT_KEYS[`ballot_receipt_${key}_title` as keyof typeof FREETEXT_KEYS]
    );
    const renderCloseButton = isStandAdmin;
    const closeBtnText = freetextStore.getFreetextValueByKey(FREETEXT_KEYS.stand_admin_receipt_btn);
    const buttonText = freetextStore.getFreetextValueByKey(
        FREETEXT_KEYS[`ballot_receipt_${key}_btn` as keyof typeof FREETEXT_KEYS]
    );
    const url = freetextStore.getFreetextValueByKey(
        FREETEXT_KEYS[`ballot_receipt_${key}_btn_link` as keyof typeof FREETEXT_KEYS]
    );
    const message = isStandAdmin
        ? undefined
        : freetextStore.getFreetextValueByKey(
              FREETEXT_KEYS[`ballot_receipt_${key}_message` as keyof typeof FREETEXT_KEYS],
              {
                  noFallbackToKey: true,
              }
          );

    return (
        <SubmitVoteMessage
            type={type}
            title={title}
            message={message}
            buttonText={buttonText}
            renderCloseButton={renderCloseButton}
            url={url}
            closeBtnText={closeBtnText}
        />
    );
});

export default SubmitVoteMessageWithStore;
